"use client"

import dynamic from "next/dynamic"
import { useState, useEffect } from "react"
import { Navbar } from "@/components/navbar"
import { Hero } from "@/components/hero"
import { motion, AnimatePresence } from "framer-motion"
import { ScrollTrigger } from "gsap/ScrollTrigger"

const CustomCursor = dynamic(() => import("@/components/custom-cursor").then(mod => mod.CustomCursor), { ssr: false })
const Vault = dynamic(() => import("@/components/vault").then(mod => mod.Vault), { ssr: false })
const RecruiterTool = dynamic(() => import("@/components/recruiter-tool").then(mod => mod.RecruiterTool), { ssr: false })
const About = dynamic(() => import("@/components/about").then(mod => mod.About), { ssr: false })
const Works = dynamic(() => import("@/components/works").then(mod => mod.Works), { ssr: false })
const TechStack = dynamic(() => import("@/components/tech-stack").then(mod => mod.TechStack), { ssr: false })
const Footer = dynamic(() => import("@/components/footer").then(mod => mod.Footer), { ssr: false })

export function HomeClient() {
  const [triggerCopy, setTriggerCopy] = useState(false)
  const [isVaultOpen, setIsVaultOpen] = useState(false)
  const [hasOpenedVault, setHasOpenedVault] = useState(false)
  const [isExploring, setIsExploring] = useState(false)
  const [showInterior, setShowInterior] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [loadCursor, setLoadCursor] = useState(false)
  
  // Staggered loading states for performance and fluidity
  const [stages, setStages] = useState({
    about: false,
    works: false,
    stack: false,
    footer: false
  })

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleHeroReady = () => {
    // Sequentially load components with smooth entrance
    setTimeout(() => setStages(prev => ({ ...prev, about: true })), 100);
    setTimeout(() => setStages(prev => ({ ...prev, works: true })), 600);
    setTimeout(() => setStages(prev => ({ ...prev, stack: true })), 1100);
    setTimeout(() => {
      setStages(prev => ({ ...prev, footer: true }));
      // CRITICAL: Final refresh to calibrate all GSAP triggers once layout is stable
      setTimeout(() => {
        if (typeof window !== "undefined") {
          ScrollTrigger.refresh();
        }
      }, 1000);
    }, 1600);
  }

  useEffect(() => {
    const load = () => setLoadCursor(true)
    window.addEventListener("pointermove", load, { once: true, passive: true })
    return () => window.removeEventListener("pointermove", load)
  }, [])

  const handleAiContextClick = () => {
    setTriggerCopy(true)
    setTimeout(() => setTriggerCopy(false), 100)
  }

  const handleOpenVault = () => {
    setHasOpenedVault(true)
    setIsVaultOpen(true)
  }

  const handlePortfolioClick = () => {
    if (isExploring) return
    setIsExploring(true)
    window.history.pushState(null, "", "#portfolio")
    setTimeout(() => {
      setShowInterior(true)
    }, 1100)
  }

  const handleExitPortfolio = () => {
    setShowInterior(false)
    setIsExploring(false)
    window.history.pushState(null, "", window.location.pathname)
  }

  return (
    <div className="relative z-10">
      {mounted && loadCursor && <CustomCursor />}
      <Navbar 
        onAiContextClick={handleAiContextClick} 
        onContactClick={handleOpenVault}
        onPortfolioClick={handlePortfolioClick}
      />
      <RecruiterTool triggerCopy={triggerCopy} />
      {hasOpenedVault && <Vault isOpen={isVaultOpen} onClose={() => setIsVaultOpen(false)} />}
      <main>
        <Hero 
          onAiContextClick={handleAiContextClick} 
          onContactClick={handleOpenVault}
          isExploring={isExploring}
          showInterior={showInterior}
          onExplore={handlePortfolioClick}
          onExit={handleExitPortfolio}
          onReady={handleHeroReady}
        />
        
        <div className="relative">
          <AnimatePresence>
            {stages.about && (
              <motion.div 
                key="about"
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
              >
                <About />
              </motion.div>
            )}
            {stages.works && (
              <motion.div 
                key="works"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ duration: 1.2, delay: 0.2 }}
              >
                <Works />
              </motion.div>
            )}
            {stages.stack && (
              <motion.div 
                key="stack"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ duration: 1.2, delay: 0.4 }}
              >
                <TechStack />
              </motion.div>
            )}
            {stages.footer && (
              <motion.div 
                key="footer"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ duration: 1.2, delay: 0.6 }}
              >
                <Footer onCollaborateClick={handleOpenVault} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  )
}
